import type { ReactNode } from "react";
import { useMemo } from "react";

import type { ComponentFlowRow, ComponentGroup } from "@busright/data-contract";
import { COLORS, DynamicChart, type DynamicChartRow } from "@busright/ui";

import { formatInt } from "@/lib/format";

// R4 — replacements by month × component × purpose, restricted to the replacement class (Replacement
// + Pick-and-Pack order purposes). Pivoted into one row per month with a column per component so
// `DynamicChart` gives the same switchable-axis interaction as the purchases chart. The purpose
// breakdown (Replacement vs Pick-and-Pack) is surfaced as a totals table underneath since a 4×N
// purpose split doesn't read well as bars once component is already the switchable dimension.

const COMPONENT_LABEL: Record<ComponentGroup, string> = {
  tablet: "Tablet",
  cradle: "Cradle / mount",
  harness: "Charging harness",
  sim: "SIM",
};

const COMPONENT_ORDER: readonly ComponentGroup[] = ["tablet", "cradle", "harness", "sim"];

export interface ScReplacementFlowProps {
  readonly rows: readonly ComponentFlowRow[];
  readonly height?: number;
}

export const ScReplacementFlow = ({ rows, height = 240 }: ScReplacementFlowProps): ReactNode => {
  const replacementRows = useMemo(() => rows.filter((r) => r.isReplacementClass), [rows]);

  const chartRows = useMemo<DynamicChartRow[]>(() => {
    const byMonth = new Map<string, Record<ComponentGroup, number>>();
    for (const r of replacementRows) {
      const existing = byMonth.get(r.month) ?? { tablet: 0, cradle: 0, harness: 0, sim: 0 };
      existing[r.component] += r.componentQty;
      byMonth.set(r.month, existing);
    }
    return [...byMonth.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, totals]) => ({ month, ...totals }));
  }, [replacementRows]);

  const byPurpose = useMemo(() => {
    const totals = new Map<string, number>();
    for (const r of replacementRows) {
      totals.set(r.orderPurpose, (totals.get(r.orderPurpose) ?? 0) + r.componentQty);
    }
    return [...totals.entries()].sort(([, a], [, b]) => b - a);
  }, [replacementRows]);

  if (replacementRows.length === 0) {
    return null;
  }

  return (
    <div>
      <DynamicChart
        rows={chartRows}
        dimensions={[{ key: "month", label: "Month" }]}
        metrics={COMPONENT_ORDER.map((c) => ({ key: c, label: COMPONENT_LABEL[c], unit: "units" }))}
        color={COLORS.accent}
        height={height}
      />
      <div className="metric-strip" style={{ marginTop: 12 }}>
        {byPurpose.map(([purpose, qty]) => (
          <div key={purpose} className="metric-chip">
            <span className="mc-label">{purpose}</span>
            <span className="mc-value br-tabular-nums">{formatInt(qty)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
